/**
 * StreakFreezeDialog — PROACTIVE vacation-mode freeze.
 *
 * Lets premium users (Pro / Duo / Founding) hold their streak in place
 * for 1-N days while they're offline (holiday, hospital, etc). On expiry
 * the streak resumes from where they left off, no save credit spent.
 *
 * Premium-only: free tier sees an Upgrade CTA. Limits per plan come from
 * `/api/auth/streak-status.freeze_config`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Snowflake, Lock, Sparkles, Calendar } from "lucide-react";
import { API } from "@/App";

export default function StreakFreezeDialog({ open, onOpenChange, onFrozen }) {
  const [status, setStatus] = useState(null);
  const [days, setDays] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/streaks/status`);
        if (cancelled) return;
        setStatus(res.data);
        setDays(1);
      } catch (e) {
        if (cancelled) return;
        toast.error("Couldn't load freeze status");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const cfg = status?.freeze_config;
  const planId = status?.plan_id || "free";
  const isFree = planId === "free";
  const maxDays = cfg?.max_freeze_days || 0;
  const cooldownDays = cfg?.cooldown_days || 0;
  const cooldownActive = !!cfg?.cooldown_active;
  const nextAvailableAt = cfg?.next_available_at;
  const isFrozen = status?.is_frozen;

  // Pretty-print the next-available date
  const nextAvailableLabel = nextAvailableAt
    ? new Date(nextAvailableAt).toLocaleDateString("en-GB", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : null;

  const handleFreeze = async () => {
    if (isFree) {
      setShowPaywall(true);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/streaks/freeze`, { days });
      if (res.data.success) {
        toast.success(`Streak frozen for ${res.data.days} day${res.data.days > 1 ? "s" : ""}`);
        onFrozen?.(res.data);
        onOpenChange(false);
      } else {
        toast.error(res.data.error || "Couldn't freeze your streak");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || "Couldn't freeze your streak");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm bg-card border-none rounded-2xl"
        data-testid="streak-freeze-dialog"
      >
        <DialogHeader>
          <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 mb-2">
            <Snowflake className="w-7 h-7 text-cyan-400" />
          </div>
          <DialogTitle className="font-serif text-xl text-center">
            Freeze your streak
          </DialogTitle>
          <DialogDescription className="text-center">
            Going off-grid for a few days? Freeze your streak and resume right where you left off.
          </DialogDescription>
        </DialogHeader>

        {!status ? (
          <p className="text-center text-sm text-muted-foreground py-4">Loading…</p>
        ) : isFrozen ? (
          <div className="space-y-2 py-2 text-center">
            <p className="text-sm">
              Your streak is already frozen until
              {" "}
              <strong>{new Date(status.freeze_expires_at).toLocaleDateString("en-GB", { month: "short", day: "numeric" })}</strong>.
            </p>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full">Got it</Button>
          </div>
        ) : isFree ? (
          /* Free-tier paywall teaser */
          <div className="space-y-3" data-testid="streak-freeze-paywall">
            <div className="rounded-xl bg-muted/30 px-4 py-4 text-sm space-y-2">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                <span className="font-semibold">Streak Freeze is a premium perk</span>
              </div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Sparkles className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />
                  Freeze your streak for up to 7 days at a time
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />
                  Reusable every 60 days — perfect for vacations
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles className="w-3 h-3 mt-0.5 text-primary flex-shrink-0" />
                  Duo plan: both partners protected during the same trip
                </li>
              </ul>
            </div>
            <Button
              onClick={() => { window.location.href = "/pricing?trigger=streak_freeze"; }}
              className="w-full bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl h-11"
              data-testid="streak-freeze-upgrade-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Upgrade to unlock Streak Freeze
            </Button>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full rounded-xl">
              Not now
            </Button>
          </div>
        ) : cooldownActive ? (
          /* In cooldown — already used a freeze recently */
          <div className="space-y-3 py-2 text-center" data-testid="streak-freeze-cooldown">
            <div className="rounded-xl bg-muted/30 px-4 py-3 text-sm">
              <p className="text-muted-foreground">
                You used a freeze recently. Your next freeze is available on
              </p>
              <p className="font-semibold text-foreground mt-1">{nextAvailableLabel}</p>
              <p className="text-[11px] text-muted-foreground mt-2">
                Streak Freeze is reusable every {cooldownDays} days so vacation-mode stays meaningful.
              </p>
            </div>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full">Got it</Button>
          </div>
        ) : (
          /* Premium picker */
          <div className="space-y-4">
            <div className="rounded-xl bg-muted/30 px-4 py-3 text-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Max length per freeze</span>
                <span className="font-semibold">{maxDays} days</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Reusable every</span>
                <span className="font-semibold">{cooldownDays} days</span>
              </div>
              {cfg?.has_shared_protection && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Partner</span>
                  <span className="font-semibold">Also protected ✨</span>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Freeze for how many days?
              </label>
              <div className="flex gap-2 flex-wrap">
                {Array.from({ length: maxDays }, (_, i) => i + 1).map((d) => (
                  <button
                    key={d}
                    onClick={() => setDays(d)}
                    className={`flex-1 min-w-[58px] py-2 rounded-lg text-sm font-medium border transition-all ${
                      days === d
                        ? "bg-cyan-500/20 border-cyan-500 text-foreground"
                        : "border-border text-muted-foreground hover:border-cyan-500/50"
                    }`}
                    data-testid={`freeze-day-${d}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={handleFreeze}
              disabled={submitting}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl h-11"
              data-testid="streak-freeze-confirm"
            >
              <Snowflake className="w-4 h-4 mr-2" />
              {submitting ? "Freezing…" : `Freeze for ${days} day${days > 1 ? "s" : ""}`}
            </Button>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full rounded-xl h-9">
              Cancel
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
